/**
 * What an operator can do to one watched site from anywhere it is shown: check
 * it now, pause or resume it, remove it.
 *
 * The desk's row menu and the site page offer the same three, so the requests,
 * the busy flag and the sentences for a refusal live here once. Every result is
 * said in a toast - an action whose only effect is a pill changing colour on a
 * row the operator was not looking at reads as nothing happening.
 *
 * Check now is refused within 30 s of the last check (`too_soon`) and while the
 * engine is already checking the same site (`busy`). Both are normal answers,
 * not failures, and are worded that way. So is `our_network`: the server makes
 * no check at all while our own network is down, rather than record a "down"
 * that was ours. And so is `engine_off`: nothing is checked while the engine
 * is switched off.
 *
 * A check that reaches a site during a confirmed outage does not end it: the
 * outage closes after two answers in a row. The pill still says down, so the
 * toast says what happened instead of repeating "down" - this is the moment a
 * customer has just phoned to say the site is back.
 */
import { useCallback, useState } from "react";
import { useT } from "../../../i18n/I18nProvider";
import {
  checkMonitorNow,
  deleteMonitor,
  monitorErrorKey,
  updateMonitor,
  type MonitorRow,
} from "../../../lib/monitoring";
import { useToast } from "../../ui";
import { codeText } from "./words";

export function useMonitorActions({
  onChanged,
  onRemoved,
}: {
  onChanged: (site: MonitorRow) => void;
  onRemoved: (id: string) => void;
}) {
  const t = useT();
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  const run = useCallback(
    async (id: string, work: () => Promise<void>) => {
      setBusyId(id);
      try {
        await work();
      } catch (err) {
        toast(t(`monitoring.error.${monitorErrorKey(err)}`));
      } finally {
        setBusyId(null);
      }
    },
    [t, toast],
  );

  const checkNow = useCallback(
    (site: MonitorRow) =>
      run(site.id, async () => {
        const res = await checkMonitorNow(site.id);
        onChanged(res.site);
        const checked = res.site;
        const said = codeText(t, checked.lastCode);
        const recovering = checked.state === "down" && checked.openIncident !== null && checked.lastCode === null;
        toast(
          checked.state === "up"
            ? t("monitoring.toast.checkedUp")
            : recovering
              ? t("monitoring.toast.checkedRecovering")
              : t("monitoring.toast.checkedOther", { what: said ?? t(`monitoring.state.${checked.state}`) }),
        );
      }),
    [run, onChanged, t, toast],
  );

  const setPaused = useCallback(
    (site: MonitorRow, paused: boolean) =>
      run(site.id, async () => {
        const res = await updateMonitor(site.id, { paused });
        onChanged(res.site);
        // Pausing a site that is down ends its outage there and then (the
        // server closes it, and sends no "back up" - nobody saw it come back).
        // Said out loud, because the red row turning grey is otherwise easy to
        // read as the site having recovered.
        toast(
          t(
            !paused
              ? "monitoring.toast.resumed"
              : site.openIncident
                ? "monitoring.toast.pausedClosed"
                : "monitoring.toast.paused",
          ),
        );
      }),
    [run, onChanged, t, toast],
  );

  const remove = useCallback(
    (site: MonitorRow) =>
      run(site.id, async () => {
        await deleteMonitor(site.id);
        onRemoved(site.id);
        toast(t("monitoring.toast.removed", { host: site.host }));
      }),
    [run, onRemoved, t, toast],
  );

  return { busyId, checkNow, setPaused, remove };
}
